import { debounce } from 'throttle-debounce'
import { Controller } from 'stimulus'
import { getIframeInParentDoc } from '~/common/helpers/iframe'

export default class extends Controller {
  static targets = ['form']

  submitForm() {
    this.formTarget.submit()
  }

  // E.g:
  //   stimulus_controller('utils', actions: { change: 'dispatchEvent' }, params: { name: 'orderChanged', to: '.something-something', include_target_value: true })
  dispatchEvent = (event) => {
    const {
      name,
      to,
      detail,
      forwardDetail,
      includeTargetValue,
      unlessClosest,
    } = event.params || {}

    if (unlessClosest && event.target.closest(unlessClosest)) return

    const eventOptions = { prefix: false, detail: detail || {} }

    if (to) {
      if (to == 'parent') eventOptions.target = this.element.parentElement
      else if (to == 'parentFrame') eventOptions.target = getIframeInParentDoc()
      else if (to.endsWith('>iframeContentDocument')) {
        const iframeSelector = to.replace('>iframeContentDocument', '')
        eventOptions.target =
          document.querySelector(iframeSelector).contentDocument
      } else eventOptions.target = document.querySelector(to)
      if (!eventOptions.target) {
        console.log('Cannot find element with selector', to)
      }
    }
    if (forwardDetail && event.detail) {
      eventOptions.detail = { ...event.detail, ...eventOptions.detail } // detail in params takes precedence (if any)
    }
    if (includeTargetValue && !eventOptions.detail.value) {
      eventOptions.detail.value = event.target.value
    }

    this.dispatch(name, eventOptions)
  }

  debouncedDispatchEvent(event) {
    const params = { ...event.params } // capture before Stimulus overwrites event.params for subsequent action handlers
    const delay = params.debounceDelay || 300

    this.dispatchEventDebouncedByDelay =
      this.dispatchEventDebouncedByDelay || {}

    this.dispatchEventDebouncedByDelay[delay] =
      this.dispatchEventDebouncedByDelay[delay] ||
      debounce(delay, this.dispatchEvent)

    this.dispatchEventDebouncedByDelay[delay]({
      target: event.target,
      detail: event.detail,
      params,
    })
  }

  // Opens a URL in a new tab on click. Honors the same `unlessClosest` guard as dispatchEvent,
  // so clicks inside e.g. a `.js-dropdown` kebab are ignored and the dropdown keeps working.
  // E.g:
  //   stimulus_controller('utils', actions: { click: 'openInNewTab' }, params: { url: occurrence.show_url, unless_closest: '.js-dropdown' })
  openInNewTab(event) {
    const { url, unlessClosest } = event.params || {}

    if (!url) return
    if (unlessClosest && event.target.closest(unlessClosest)) return

    window.open(url, '_blank', 'noopener')
  }

  // Same guard semantics as openInNewTab, but navigates the current window instead of opening a
  // new one. Use this wherever a new-window request can't rely on a real anchor href — the
  // mobile app's WebView can't hit-test a non-anchor click target, so window.open() there
  // silently does nothing instead of opening (ENG-34417).
  // E.g:
  //   stimulus_controller('utils', actions: { click: 'navigateInPlace' }, params: { url: occurrence.show_url, unless_closest: '.js-dropdown' })
  navigateInPlace(event) {
    const { url, unlessClosest } = event.params || {}

    if (!url) return
    if (unlessClosest && event.target.closest(unlessClosest)) return

    window.location.href = url
  }

  // Currently forwards a click event to the first child link (assuming the click was itself not on a interactable element like input/button/a tag).
  // Feel free to extend to add options as more things are needed (e.g forwarding to custom target, changing conditions for when we should forward)
  forwardClick(event) {
    if (event.target.closest('.js-no-click-forwarding')) return

    const closestClickable = event.target.closest(
      'button, a, input, textarea, select, .js-forward-click-target, .js-dropdown, .switch-wrapper, .js-switch'
    )

    // We probably clicked on a clickable element, so we don't want to forward that click
    // We explicitly check that its not the controller element because, in some cases you do want a button for e.g to forward its click to some other target
    // E.g clicking on a button to open a file picker
    if (
      closestClickable &&
      closestClickable != this.element &&
      this.element.contains(closestClickable)
    )
      return

    const target =
      (event.params?.to
        ? this.element.querySelector(event.params.to) ||
          document.querySelector(event.params.to)
        : null) ||
      event.target.querySelector('.js-forward-click-target') ||
      event.currentTarget.querySelector('.js-forward-click-target') ||
      event.target.querySelector('a, button') ||
      event.currentTarget.querySelector('a, button')

    if (!target) return

    if (target.href && (event.ctrlKey || event.metaKey || event.button === 1)) {
      window.open(target.href, '_blank')
    } else if (event.params?.focus) {
      target.focus()
    } else {
      target.click()
    }
  }

  resetForm(event) {
    document.querySelector(event.params.resetFormSelector)?.reset()
  }

  preventDefault(event) {
    event.preventDefault()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  focus(event) {
    const element = document.getElementById(event.params.target)
    if (element) element.focus()
  }
}
